import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import "@/pages/admin/dokumenter/dokumenter.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DokShare() {
  const { token } = useParams();
  const [tilstand, setTilstand] = useState("laster");
  const [tittel, setTittel] = useState("");
  const [kode, setKode] = useState("");
  const [feil, setFeil] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/docs/s/${token}`)
      .then(({ data: d }) => { setTittel(d.title || ""); setTilstand("laast"); })
      .catch(() => setTilstand("borte"));
  }, [token]);

  const laasOpp = async (e) => {
    e.preventDefault();
    setFeil("");
    try {
      const { data: d } = await axios.post(`${API}/docs/s/${token}/unlock`, { code: kode });
      setData(d.data);
      setTilstand("aapen");
    } catch {
      setFeil("Feil kode — prøv igjen.");
    }
  };

  if (tilstand === "laster") return <div className="min-h-screen bg-[#0b0f14]" />;

  if (tilstand === "borte") {
    return (
      <div className="min-h-screen bg-[#0b0f14] flex items-center justify-center px-6">
        <p className="text-[14px] text-neutral-400" data-testid="dok-share-borte">Denne lenken er ikke lenger aktiv.</p>
      </div>
    );
  }

  if (tilstand === "laast") {
    return (
      <div className="min-h-screen bg-[#0b0f14] flex items-center justify-center px-6">
        <form onSubmit={laasOpp} className="w-full max-w-[360px] text-center" data-testid="dok-share-laast">
          <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-neutral-500 mb-3">Beskyttet dokument</p>
          <h1 className="text-[22px] font-semibold text-[#f5f1e8] leading-snug mb-6">{tittel}</h1>
          <input
            value={kode}
            onChange={(e) => setKode(e.target.value.toUpperCase())}
            placeholder="TILGANGSKODE"
            maxLength={6}
            autoFocus
            className="w-full text-center font-mono text-[18px] tracking-[0.4em] bg-transparent border border-neutral-700 rounded-xl px-4 py-3 text-[#f5f1e8] placeholder:text-neutral-600 focus:outline-none focus:border-neutral-400 transition-colors"
            data-testid="dok-share-kode"
          />
          {feil && <p className="text-[12px] text-red-400 mt-3" data-testid="dok-share-feil">{feil}</p>}
          <button type="submit" className="mt-5 w-full bg-[#f5f1e8] text-[#14171c] text-[13px] font-semibold rounded-xl py-3 hover:bg-white transition-colors" data-testid="dok-share-aapne">
            Åpne dokumentet
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F5] dok-share" data-testid="dok-share-innhold">
      <div className="max-w-[760px] mx-auto px-5 sm:px-6 py-10 sm:py-20">
        <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-neutral-400 mb-5">
          {data.eventName} — Delt dokument
        </p>
        {data.cover && (
          <div className="dok-share-cover" data-testid="dok-share-cover"
            style={data.cover.startsWith("linear-gradient")
              ? { background: data.cover }
              : { backgroundImage: `url(${data.cover})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        )}
        {data.icon && <div className="dok-share-ikon" data-testid="dok-share-ikon">{data.icon}</div>}
        <h1 className="text-[30px] sm:text-[44px] font-semibold tracking-[-0.03em] leading-[1.08] text-neutral-900" style={{ fontFamily: '"Schibsted Grotesk", "Switzer", "Instrument Sans", sans-serif' }}>{data.title}</h1>
        <div className="dok-share-byline" data-testid="dok-share-byline">
          {data.updatedBy && (
            <span className="inline-flex items-center gap-2">
              {data.updatedByBilde
                ? <span className="dok-byline-avatar foto"><img src={data.updatedByBilde} alt="" /></span>
                : <span className="dok-byline-avatar">{data.updatedBy[0].toUpperCase()}</span>}
              <b>{data.updatedBy}</b>
            </span>
          )}
          <span className="dok-skilledot" />
          <span>{new Date(data.updatedAt).toLocaleDateString("nb-NO", { day: "numeric", month: "long", year: "numeric" })}</span>
          {data.ord > 0 && (
            <>
              <span className="dok-skilledot" />
              <span data-testid="dok-share-ord">{data.ord} ord · {Math.max(1, Math.round(data.ord / 200))} min</span>
            </>
          )}
          {data.dataMode !== "live" && data.snapshotCreatedAt && (
            <>
              <span className="dok-skilledot" />
              <span data-testid="dok-share-snapshot">Live-data per {new Date(data.snapshotCreatedAt).toLocaleDateString("nb-NO", { day: "numeric", month: "long" })}</span>
            </>
          )}
        </div>
        {data.puls && (data.puls.relasjoner > 0 || data.puls.handlinger > 0) && (
          <div className="dok-pulsrad dok-share-puls" data-testid="dok-share-puls">
            <span><span className="dok-puls-tall">{data.puls.relasjoner}</span> targets</span>
            <span><span className="dok-puls-tall">{data.puls.now ?? 0}</span> now</span>
            <span><span className="dok-puls-tall">{data.puls.dialoger}</span> aktive dialoger</span>
            <span className={data.puls.uten ? "varsle" : ""}><span className="dok-puls-tall">{data.puls.uten ?? 0}</span> mangler eier</span>
            {data.puls.beslutninger > 0 && <span><span className="dok-puls-tall">{data.puls.beslutninger}</span> åpne beslutninger</span>}
            {data.puls.handlinger > 0 && <span><span className="dok-puls-tall">{data.puls.handlinger}</span> handlinger</span>}
          </div>
        )}
        {/* eslint-disable-next-line react/no-danger */}
        <div className="dok-prose mt-10" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.content) }} />
      </div>
    </div>
  );
}
